import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { ActivityIndicator } from '@kontentino/ui';
import Card from 'app/modules/analytics/components/shared/Card';
import {
  type FbReactionId,
  FB_REACTIONS_ORDER,
} from 'app/modules/analytics/modules/performance/components/fbReactionsConstants';
import type { FbReactionsByType } from 'app/modules/analytics/modules/performance/utils/getFacebookReactionsFromOverview';

import fbAngrySvg from 'images/svg/fbReactions/angry.svg';
import fbLikeSvg from 'images/svg/fbReactions/like.svg';
import fbLoveSvg from 'images/svg/fbReactions/love.svg';
import fbHahaSvg from 'images/svg/fbReactions/haha.svg';
import fbSadSvg from 'images/svg/fbReactions/sad.svg';
import fbWowSvg from 'images/svg/fbReactions/wow.svg';

const FB_REACTION_SVGS: Record<FbReactionId, string> = {
  angry: fbAngrySvg,
  like: fbLikeSvg,
  love: fbLoveSvg,
  haha: fbHahaSvg,
  sad: fbSadSvg,
  wow: fbWowSvg,
};

interface ReportReactionsCardProps {
  items?: FbReactionsByType;
  visibleIds?: FbReactionId[];
  align?: 'center' | 'left';
  isLoading?: boolean;
}

const ReportReactionsCard: FC<ReportReactionsCardProps> = ({
  items,
  visibleIds,
  align = 'center',
  isLoading = false,
}) => {
  const { t } = useTranslation();

  const idsToRender = visibleIds ?? FB_REACTIONS_ORDER;
  const isLeft = align === 'left';

  return (
    <div data-cy="report-reactions-card">
      <Card
        className={clsx('tw-gap-3 tw-border tw-border-grayscale-20', {
          'tw-items-start': isLeft,
          'tw-items-center': !isLeft,
        })}
      >
        <p
          className={clsx(
            'tw-w-full tw-text-md tw-font-medium tw-text-grayscale-180',
            {
              'tw-text-left': isLeft,
              'tw-text-center': !isLeft,
            },
          )}
        >
          {t('analyticsPerformance.reactions')}
        </p>
        {isLoading ? (
          <div className="tw-flex tw-min-h-[30px] tw-w-full tw-items-center tw-justify-center">
            <ActivityIndicator />
          </div>
        ) : (
          <div
            className={clsx('tw-flex tw-w-full tw-flex-wrap tw-gap-2', {
              'tw-justify-start': isLeft,
              'tw-justify-center': !isLeft,
            })}
          >
            {idsToRender.map((id) => {
              const label = t(`analyticsPerformance.fbReactionLabels.${id}`);
              const entry = items?.[id];
              if (!entry) {
                return null;
              }
              return (
                <div
                  key={id}
                  className="tw-flex tw-cursor-default tw-items-center tw-justify-center tw-gap-1 tw-rounded-md tw-px-1 tw-py-1"
                  data-cy={`report-reactions-item-${id}`}
                >
                  <span className="tw-size-[22px] tw-flex tw-shrink-0 tw-items-center tw-justify-center">
                    <img
                      src={FB_REACTION_SVGS[id]}
                      alt={label}
                      className="tw-size-full tw-object-contain"
                    />
                  </span>
                  <span className="tw-text-sm tw-font-medium tw-leading-5 tw-text-grayscale-180">
                    {entry.value}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ReportReactionsCard;
